import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointments, Appointment } from '../../crm-appointments/useData';
import { appointmentsCalendarPath } from '../../crm-appointments/paths';
import { formatTime as formatAppTime } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { listRowClass } from '../themeHelpers';
import GenericListWidget from './GenericListWidget';

export interface TodaysAppointmentsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const todayStr = new Date().toISOString().slice(0, 10);

const TodaysAppointments: React.FC<TodaysAppointmentsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { appointments, isLoading } = useAppointments();

  const items = React.useMemo(() => {
    const arr = Array.isArray(appointments) ? appointments : [];
    return arr
      .filter((a: Appointment) => {
        const dt = a.clientAppScheduleDateTime?.slice(0, 10);
        return dt === todayStr;
      })
      .sort((a: Appointment, b: Appointment) => {
        const ta = new Date(a.clientAppScheduleDateTime || 0).getTime();
        const tb = new Date(b.clientAppScheduleDateTime || 0).getTime();
        return ta - tb;
      })
      .slice(0, effectiveLimit);
  }, [appointments, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    return formatAppTime(dateString);
  };

  return (
    <GenericListWidget<Appointment>
      title="Today's Appointments"
      viewAllPath={appointmentsCalendarPath()}
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No appointments today"
      getItemKey={(a) => a.clientAppId}
      embedded={embedded}
      renderItem={(apt) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/appointments/${apt.clientAppId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/appointments/${apt.clientAppId}`)
          }
          className={listRowClass(colors)}
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {apt.clientAppName || apt.client?.clientName || 'Appointment'}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {formatTime(apt.clientAppScheduleDateTime)}
              {apt.assignedToUser?.userDisplayName &&
                ` · ${apt.assignedToUser.userDisplayName}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default TodaysAppointments;
